import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface AssetDetailSheetState {
  assetId: string | null;
  isOpen: boolean;
  defaultTab?: string;
  openAssetDetail: (assetId: string, tab?: string) => void;
  closeAssetDetail: () => void;
}

const AssetDetailSheetContext = createContext<AssetDetailSheetState | undefined>(undefined);

export function AssetDetailSheetProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [assetId, setAssetId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [defaultTab, setDefaultTab] = useState<string | undefined>(undefined);

  const openAssetDetail = useCallback((id: string, tab?: string) => {
    setAssetId(id);
    setIsOpen(true);
    setDefaultTab(tab);
  }, []);

  const closeAssetDetail = useCallback(() => {
    setAssetId(null);
    setIsOpen(false);
    setDefaultTab(undefined);
  }, []);

  const contextValue = useMemo(
    () => ({ assetId, isOpen, defaultTab, openAssetDetail, closeAssetDetail }),
    [assetId, isOpen, defaultTab, openAssetDetail, closeAssetDetail]
  );

  return (
    <AssetDetailSheetContext.Provider value={contextValue}>
      {children}
    </AssetDetailSheetContext.Provider>
  );
}

export function useAssetDetailSheet() {
  const context = useContext(AssetDetailSheetContext);
  if (!context) {
    throw new Error('useAssetDetailSheet must be used within an AssetDetailSheetProvider');
  }
  return context;
}
